import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  AlertTitle,
  IconButton,
  Chip
} from '@mui/material';
import {
  Add,
  Refresh,
  Visibility,
  ContentCopy,
  CheckCircle
} from '@mui/icons-material';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/api';
import { ResellerCreate, ResellerSummary } from '../types/reseller';
import { AccountUserAuthMethod } from '../types/clientAccount';
import AuthMethodSelect from '../components/AuthMethodSelect';
import { formatCredits } from '../utils/credits';
import LoadingButton from '../components/LoadingButton';
import LimitRequestsInbox from '../components/LimitRequestsInbox';

// Sección ADMIN «Partners»: alta y gobierno de resellers (CSP). Mismo patrón
// que ClientAccountsPage: tabla + alta con diálogo + contraseña one-time.

// Porcentajes sin ceros de relleno («12.5 %», no «12.50 %»).
export const formatPct = (value: number | string | null | undefined): string => {
  const num = Number(value);
  return Number.isNaN(num) ? String(value ?? '0') : String(parseFloat(num.toFixed(2)));
};

interface CreateForm {
  name: string;
  contact_email: string;
  user_email: string;
  user_full_name: string;
  notes: string;
  // Cómo entrará al portal el PRIMER usuario del partner.
  auth_method: AccountUserAuthMethod;
}

const emptyForm: CreateForm = {
  name: '',
  contact_email: '',
  user_email: '',
  user_full_name: '',
  notes: '',
  auth_method: 'password'
};

const PartnersPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [partners, setPartners] = useState<ResellerSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Alta de partner
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createData, setCreateData] = useState<CreateForm>({ ...emptyForm });
  // Numéricos como string para validar el input (contrato: pct 0..100, cupo ≥1, techo ≥0)
  const [createDiscount, setCreateDiscount] = useState('0');
  const [createMaxClients, setCreateMaxClients] = useState('10');
  const [createMaxWholesale, setCreateMaxWholesale] = useState('0');
  // Cupo AGREGADO de empleados IA de toda su cartera (0 = sin límite).
  const [createMaxEmployees, setCreateMaxEmployees] = useState('0');
  // Techo AGREGADO de almacenamiento (GiB) de toda su cartera. 0 = sin límite,
  // mismo criterio que el de empleados y el de bolsa mayorista.
  const [createMaxStorage, setCreateMaxStorage] = useState('0');
  // Incluido sin coste del PARTNER (default 20 = un empleado entero): lo que
  // pase de ahí se le factura a él por ciclo, no a sus clientes.
  const [createIncludedStorage, setCreateIncludedStorage] = useState('20');

  // Contraseña generada del primer usuario del portal — se muestra UNA vez
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [createdInfo, setCreatedInfo] = useState<{
    partner_name: string;
    user_email: string;
    generated_password: string;
    // El backend intenta enviarla también al buzón del usuario del portal; si no
    // salió, se mantiene el texto de siempre: entrégala tú por un canal seguro.
    email_sent: boolean;
  } | null>(null);
  // Alta SOLO-GOOGLE: no hay contraseña que enseñar, pero el admin tiene que
  // saber que el partner ya puede entrar y con qué cuenta.
  const [googleNotice, setGoogleNotice] = useState<string | null>(null);

  const loadPartners = async () => {
    try {
      setError('');
      const data = await apiService.getResellers();
      setPartners(data);
    } catch (err: any) {
      console.error('Failed to load partners:', err);
      setError(err.detail || t('partners.loadError', 'No se pudieron cargar los partners'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPartners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isValidEmail = (email: string) => /\S+@\S+\.\S+/.test(email);
  const discountValid =
    createDiscount.trim() !== '' &&
    !Number.isNaN(Number(createDiscount)) &&
    Number(createDiscount) >= 0 &&
    Number(createDiscount) <= 100;
  const maxClientsValid = /^\d+$/.test(createMaxClients.trim()) && Number(createMaxClients) >= 1;
  const maxWholesaleValid =
    createMaxWholesale.trim() !== '' &&
    !Number.isNaN(Number(createMaxWholesale)) &&
    Number(createMaxWholesale) >= 0;
  // 0 vale: es «sin límite», igual que en el techo de bolsa mayorista.
  const maxEmployeesValid = /^\d+$/.test(createMaxEmployees.trim()) && Number(createMaxEmployees) >= 0;
  const maxStorageValid = /^\d+$/.test(createMaxStorage.trim()) && Number(createMaxStorage) >= 0;
  // 0 SÍ vale y no significa «sin límite»: es «se le factura desde el primer GiB».
  const includedStorageValid =
    /^\d+$/.test(createIncludedStorage.trim()) && Number(createIncludedStorage) >= 0;
  const userEmailValid = createData.user_email.trim() === '' || isValidEmail(createData.user_email);
  const canCreate =
    createData.name.trim() !== '' &&
    isValidEmail(createData.contact_email) &&
    userEmailValid &&
    discountValid &&
    maxClientsValid &&
    maxWholesaleValid &&
    maxEmployeesValid &&
    maxStorageValid &&
    includedStorageValid;

  const handleCreatePartner = async () => {
    try {
      setCreateLoading(true);
      const payload: ResellerCreate = {
        name: createData.name.trim(),
        contact_email: createData.contact_email.trim(),
        wholesale_discount_pct: Number(createDiscount),
        max_clients: Number(createMaxClients),
        max_wholesale_credits: Number(createMaxWholesale),
        max_total_employees: Number(createMaxEmployees),
        max_total_storage_gib: Number(createMaxStorage),
        included_storage_gib: Number(createIncludedStorage),
        auth_method: createData.auth_method
      };
      if (createData.user_email.trim()) payload.user_email = createData.user_email.trim();
      if (createData.user_full_name.trim()) payload.user_full_name = createData.user_full_name.trim();
      if (createData.notes.trim()) payload.notes = createData.notes.trim();

      const response = await apiService.createReseller(payload);
      toast.success(t('partners.created', 'Partner creado'));
      setCreateDialogOpen(false);
      setCreateData({ ...emptyForm });
      setCreateDiscount('0');
      setCreateMaxClients('10');
      setCreateMaxWholesale('0');
      setCreateMaxEmployees('0');
      setCreateMaxStorage('0');
      setCreateIncludedStorage('20');

      if (response.generated_password) {
        setCreatedInfo({
          partner_name: response.reseller.name,
          user_email: response.user_email,
          generated_password: response.generated_password,
          email_sent: response.email_sent === true
        });
        setPasswordDialogOpen(true);
      } else if ((response.auth_methods || []).includes('google')) {
        setGoogleNotice(
          response.email_sent
            ? t(
                'partners.googleOnlyCreatedEmailed',
                '{{email}} entrará con su cuenta de Google. Le hemos enviado un correo de bienvenida: no hay contraseña que entregar.',
                { email: response.user_email }
              )
            : t(
                'partners.googleOnlyCreated',
                '{{email}} entrará con su cuenta de Google: no hay contraseña que entregar.',
                { email: response.user_email }
              )
        );
      }

      loadPartners();
    } catch (err: any) {
      console.error('Failed to create partner:', err);
      toast.error(err.detail || t('partners.createError', 'No se pudo crear el partner'));
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          {t('partners.title', 'Partners')}
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => loadPartners()}
            size="small"
          >
            {t('common.refresh', 'Actualizar')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateDialogOpen(true)}
          >
            {t('partners.createPartner', 'Nuevo partner')}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* BANDEJA: solicitudes de ampliación de techo pendientes. Va ARRIBA del
          listado porque es trabajo que espera decisión, no consulta. Se oculta
          sola cuando no hay nada pendiente. Al resolver una, se recarga el
          listado: el techo del partner acaba de cambiar. */}
      <LimitRequestsInbox onDecided={() => loadPartners()} />

      {/* Tabla de partners */}
      {isLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          {t('common.loading', 'Cargando...')}
        </Box>
      ) : partners.length === 0 ? (
        <Card>
          <CardContent>
            <Typography align="center" color="text.secondary" py={4}>
              {t('partners.noPartners', 'Todavía no hay partners. Crea el primero con «Nuevo partner».')}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('partners.fields.name', 'Nombre')}</TableCell>
                <TableCell>{t('partners.fields.contactEmail', 'Email de contacto')}</TableCell>
                <TableCell>{t('partners.fields.status', 'Estado')}</TableCell>
                <TableCell align="right">{t('partners.fields.wholesaleBalance', 'Bolsa mayorista')}</TableCell>
                <TableCell align="right">{t('partners.fields.clients', 'Clientes')}</TableCell>
                <TableCell align="right">{t('partners.fields.employees', 'Empleados IA')}</TableCell>
                <TableCell align="right">{t('partners.fields.storage', 'Almacenamiento')}</TableCell>
                <TableCell align="right">{t('partners.fields.discount', 'Descuento')}</TableCell>
                <TableCell>{t('partners.fields.createdAt', 'Creado')}</TableCell>
                <TableCell align="right">{t('partners.fields.actions', 'Acciones')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {partners.map((partner) => (
                <TableRow
                  key={partner.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/partners/${partner.id}`)}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {partner.name}
                    </Typography>
                  </TableCell>
                  <TableCell>{partner.contact_email}</TableCell>
                  <TableCell>
                    <Chip
                      label={partner.status === 'active'
                        ? t('partners.status.active', 'Activo')
                        : t('partners.status.suspended', 'Suspendido')}
                      color={partner.status === 'active' ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600}>
                      {formatCredits(partner.wholesale_balance)} {t('partners.creditsUnit', 'créditos')}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {partner.clients_count !== null && partner.clients_count !== undefined
                      ? `${partner.clients_count}/${partner.max_clients}`
                      : `—/${partner.max_clients}`}
                  </TableCell>
                  {/* Ocupación del cupo agregado; sin límite (0) solo el usado. */}
                  <TableCell align="right">
                    {partner.employees_count === null || partner.employees_count === undefined
                      ? '—'
                      : Number(partner.max_total_employees) > 0
                        ? `${partner.employees_count}/${partner.max_total_employees}`
                        : `${partner.employees_count} (${t('partners.noEmployeeCapShort', 'sin límite')})`}
                  </TableCell>
                  {/* Mismo criterio con el disco: usado/techo, o usado a secas
                      cuando no tiene techo (0). */}
                  <TableCell align="right">
                    {partner.storage_used_gib === null || partner.storage_used_gib === undefined
                      ? '—'
                      : Number(partner.max_total_storage_gib) > 0
                        ? `${partner.storage_used_gib}/${partner.max_total_storage_gib} GiB`
                        : `${partner.storage_used_gib} GiB (${t('partners.noEmployeeCapShort', 'sin límite')})`}
                  </TableCell>
                  <TableCell align="right">{formatPct(partner.wholesale_discount_pct)} %</TableCell>
                  <TableCell>{format(new Date(partner.created_at), 'dd/MM/yyyy')}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/partners/${partner.id}`);
                      }}
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Diálogo de alta de partner */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('partners.createPartner', 'Nuevo partner')}</DialogTitle>
        <DialogContent>
          {/* Cómo funciona el negocio del partner — visible ANTES de rellenar
              nada, para que el alta se haga entendiendo el modelo. */}
          <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
            <AlertTitle>{t('partners.businessModelTitle', 'Cómo funciona el modelo partner')}</AlertTitle>
            {t(
              'partners.businessModelInfo',
              'El partner compra créditos a precio de lista menos su descuento: ese descuento es su margen. Tú le facturas mensualmente sus compras con el descuento aplicado; él decide el precio al que asigna créditos a sus clientes.'
            )}
          </Alert>
          <TextField
            autoFocus
            margin="dense"
            label={t('partners.fields.name', 'Nombre') + ' *'}
            fullWidth
            variant="outlined"
            value={createData.name}
            onChange={(e) => setCreateData({ ...createData, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label={t('partners.fields.contactEmail', 'Email de contacto') + ' *'}
            type="email"
            fullWidth
            variant="outlined"
            value={createData.contact_email}
            onChange={(e) => setCreateData({ ...createData, contact_email: e.target.value })}
            error={createData.contact_email !== '' && !isValidEmail(createData.contact_email)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label={t('partners.fields.userEmail', 'Email del usuario del portal') + ' (' + t('common.optional', 'Opcional') + ')'}
            type="email"
            fullWidth
            variant="outlined"
            value={createData.user_email}
            onChange={(e) => setCreateData({ ...createData, user_email: e.target.value })}
            error={!userEmailValid}
            helperText={t('partners.fields.userEmailHelp', 'Primer usuario del portal partner. Vacío = se usa el email de contacto')}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label={t('partners.fields.userFullName', 'Nombre del usuario del portal') + ' (' + t('common.optional', 'Opcional') + ')'}
            fullWidth
            variant="outlined"
            value={createData.user_full_name}
            onChange={(e) => setCreateData({ ...createData, user_full_name: e.target.value })}
            helperText={t('partners.fields.userFullNameHelp', 'Vacío = se usa el nombre comercial del partner')}
            sx={{ mb: 2 }}
          />
          {/* Cómo entrará ese usuario al portal partner. Con «Solo Google» el
              alta no genera ninguna contraseña. */}
          <AuthMethodSelect
            value={createData.auth_method}
            onChange={(value) => setCreateData({ ...createData, auth_method: value })}
            subject="partner"
          />
          <TextField
            margin="dense"
            label={t('partners.fields.discount', 'Descuento') + ' (%)'}
            type="number"
            fullWidth
            variant="outlined"
            value={createDiscount}
            onChange={(e) => setCreateDiscount(e.target.value)}
            inputProps={{ min: 0, max: 100, step: '0.01' }}
            error={!discountValid}
            helperText={!discountValid
              ? t('partners.discountInvalid', 'Debe ser un porcentaje entre 0 y 100')
              : t('partners.fields.discountHelp', 'Descuento mayorista sobre el precio de lista de los créditos')}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label={t('partners.fields.maxClients', 'Máx. clientes')}
            type="number"
            fullWidth
            variant="outlined"
            value={createMaxClients}
            onChange={(e) => setCreateMaxClients(e.target.value)}
            inputProps={{ min: 1, step: 1 }}
            error={!maxClientsValid}
            helperText={!maxClientsValid
              ? t('partners.maxClientsInvalid', 'Debe ser un entero mayor o igual que 1')
              : t('partners.fields.maxClientsHelp', 'Cuántas cuentas de cliente puede gestionar este partner')}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label={t('partners.fields.maxWholesale', 'Techo de bolsa mayorista')}
            type="number"
            fullWidth
            variant="outlined"
            value={createMaxWholesale}
            onChange={(e) => setCreateMaxWholesale(e.target.value)}
            inputProps={{ min: 0, step: '0.01' }}
            error={!maxWholesaleValid}
            helperText={!maxWholesaleValid
              ? t('partners.maxWholesaleInvalid', 'Debe ser un número mayor o igual que 0')
              : t('partners.fields.maxWholesaleHelp', '0 = sin techo de saldo mayorista')}
            sx={{ mb: 2 }}
          />
          {/* Cupo AGREGADO de empleados IA: es el que gobierna de verdad la
              carga del cluster (max_clients × max_employees no la acota, porque
              el cupo por cuenta lo fija el propio partner). */}
          <TextField
            margin="dense"
            label={t('partners.fields.maxTotalEmployees', 'Máx. empleados (todos sus clientes)')}
            type="number"
            fullWidth
            variant="outlined"
            value={createMaxEmployees}
            onChange={(e) => setCreateMaxEmployees(e.target.value)}
            inputProps={{ min: 0, step: 1 }}
            error={!maxEmployeesValid}
            helperText={!maxEmployeesValid
              ? t('partners.maxTotalEmployeesInvalid', 'Debe ser un entero mayor o igual que 0')
              : t('partners.fields.maxTotalEmployeesHelp',
                  'Techo de empleados IA vivos sumando TODOS sus clientes. Gobierna la carga del cluster: cada empleado es una aplicación desplegada. 0 = sin límite')}
            sx={{ mb: 2 }}
          />
          {/* Techo AGREGADO de ALMACENAMIENTO: la otra dimensión de la carga.
              Un partner puede saturar el disco de los nodos sin pasarse de
              empleados, ampliando los volúmenes de los que ya tiene. */}
          <TextField
            margin="dense"
            label={t('partners.fields.maxTotalStorage', 'Máx. almacenamiento (GiB, todos sus clientes)')}
            type="number"
            fullWidth
            variant="outlined"
            value={createMaxStorage}
            onChange={(e) => setCreateMaxStorage(e.target.value)}
            inputProps={{ min: 0, step: 1 }}
            error={!maxStorageValid}
            helperText={!maxStorageValid
              ? t('partners.maxTotalStorageInvalid', 'Debe ser un entero mayor o igual que 0')
              : t('partners.fields.maxTotalStorageHelp',
                  'Techo de GiB sumando los volúmenes de TODOS los empleados de su cartera (cada empleado ocupa dos volúmenes reales). 0 = sin límite')}
            sx={{ mb: 2 }}
          />
          {/* Almacenamiento INCLUIDO: los GiB que el marketplace le regala
              sobre el total de su cartera. El exceso se le cobra A ÉL por
              ciclo — sus clientes nunca pagan almacenamiento. */}
          <TextField
            margin="dense"
            label={t('partners.fields.includedStorage', 'Almacenamiento incluido (GiB)')}
            type="number"
            fullWidth
            variant="outlined"
            value={createIncludedStorage}
            onChange={(e) => setCreateIncludedStorage(e.target.value)}
            inputProps={{ min: 0, step: 1 }}
            error={!includedStorageValid}
            helperText={!includedStorageValid
              ? t('partners.includedStorageInvalid', 'Debe ser un entero mayor o igual que 0')
              : t('partners.fields.includedStorageHelp',
                  'GiB sin coste sobre el TOTAL de su cartera; el exceso se le cobra a él por ciclo (sus clientes no pagan almacenamiento). 20 = los dos volúmenes de un empleado, así que su primer empleado nunca genera cargo')}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label={t('partners.fields.notes', 'Notas') + ' (' + t('common.optional', 'Opcional') + ')'}
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={createData.notes}
            onChange={(e) => setCreateData({ ...createData, notes: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>{t('common.cancel', 'Cancelar')}</Button>
          <LoadingButton
            onClick={handleCreatePartner}
            loading={createLoading}
            disabled={!canCreate}
            variant="contained"
          >
            {t('common.create', 'Crear')}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Alta SOLO-GOOGLE: no hay contraseña, pero sí algo que contar */}
      <Dialog open={googleNotice !== null} onClose={() => setGoogleNotice(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircle color="success" />
            {t('partners.createdTitle', 'Partner creado correctamente')}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info">{googleNotice}</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGoogleNotice(null)} variant="contained">
            {t('common.close', 'Cerrar')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Contraseña generada — se muestra UNA sola vez */}
      <Dialog
        open={passwordDialogOpen}
        onClose={() => setPasswordDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircle color="success" />
            {t('partners.createdTitle', 'Partner creado correctamente')}
          </Box>
        </DialogTitle>
        <DialogContent>
          {createdInfo && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>{t('partners.passwordOnceTitle', 'Importante: guarda esta contraseña')}</AlertTitle>
                {t('partners.passwordOnceMessage', 'La contraseña no volverá a mostrarse. Cópiala y entrégasela al partner para entrar en su portal.')}
                {/* Solo si el correo salió de verdad (envío fail-soft en backend). */}
                {createdInfo.email_sent && (
                  <Box sx={{ mt: 1 }}>
                    {t('partners.passwordAlsoEmailed', 'También se ha enviado por correo a {{email}}', {
                      email: createdInfo.user_email
                    })}
                  </Box>
                )}
              </Alert>
              <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('partners.fields.name', 'Nombre')}
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', mb: 2 }}>
                  {createdInfo.partner_name}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('partners.fields.userEmail', 'Email del usuario del portal')}
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', mb: 2 }}>
                  {createdInfo.user_email}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('partners.fields.generatedPassword', 'Contraseña generada')}
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    {createdInfo.generated_password}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => {
                      navigator.clipboard.writeText(createdInfo.generated_password);
                      toast.success(t('partners.passwordCopied', 'Contraseña copiada al portapapeles'));
                    }}
                  >
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)} variant="contained">
            {t('partners.passwordSaved', 'He guardado la contraseña')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PartnersPage;
